'use client';

// СЛОЙ «ОПЛАТА» — оплата в нашем дизайне (docs/MULTICURRENCY-PLAN.md Э5.3, решение владельца 8:
// на нашем сайте, без перехода на страницу Stripe). Состав и поведение — ~/projects/PAY-SCREEN-2-TZ.md
// §1–§2 (решения владельца 08.10.2026): шторки подтверждения нет, слой открывается прямо с экрана
// расчёта стопкой (NATIVE-SLIDE-PLAN §11). Сверху вниз: ряд кошельков в одну строку (Express
// Checkout Element: Apple Pay / Google Pay / PayPal — что включено в админке и есть на устройстве;
// нет ни одной — ряда нет), «или оплатить картой», выбор карты (если привязана карта Stripe),
// форма карты (Payment Element: номер, срок, код), подпись о сохранении карты (вид А) или галочка
// «Сохранить карту» (вид Б), сводка плательщика с «Изменить», кнопка «Оплатить {сумма}» с замком и
// строка защиты. Рубли (Робокасса) — ОДИН лист от расчёта до кассы (billing-rub-check.tsx, PAY-FLOW-TZ §16 К6).
//
// ЭКРАН НЕ ПЕРЕСТРАИВАЕТСЯ (~/projects/PAY-FLOW-TZ.md §3, владелец 08.10): автопродление выбрано на
// экране расчёта, слой получает его готовым и выбирает вид ОДИН РАЗ при открытии — А (автопродление:
// без PayPal и без галочки), Б (разово: PayPal и галочка), В (сохранённая карта + «Другой картой»).
// Галочка меняет только состояние слоя — `elements.update` по ней не зовётся; сохранение
// применяется `update` перед `submit` (§3.3).
//
// ОТЛОЖЕННЫЙ ПЛАТЁЖ (§2, Stripe «deferred intent»): при открытии слоя сервер не нужен —
// `Elements` создаётся в `mode: 'payment'` с суммой и валютой кнопки расчёта; PaymentIntent
// создаёт ручка оплаты только по «Оплатить» (или по `confirm` кошелька): `elements.submit()` →
// `POST` → сверка суммы → `confirmPayment`. Список способов и сохранение карты у `Elements` —
// ровно как сервер ставит их у PI (правило одно: `elementsOptions`/`cardSaveOptions` в pay-flow.ts).
//
// Вид формы — токены палитры и размеры поля мастера (lib/stripe-appearance.ts): форма живёт в
// iframe Stripe, CSS-переменные туда не доходят, поэтому значения читаются при открытии слоя и при
// смене темы и уходят в форму обновлением, без пересборки (Р6).
import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useLocale, useTranslations } from 'next-intl';
// `pure` — Stripe.js грузится только вызовом loadStripe, не импортом модуля (замок Э5.5).
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Appearance, Stripe, StripeElements, StripeExpressCheckoutElementClickEvent } from '@stripe/stripe-js';
import { Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { KButton, KListBlock, KListNote, KSkeleton, KSkeletonField } from '@scriptera/ui/kit';
import type { Currency } from '@scriptera/db/currency';
import { isNativeApp, nativeBridge, nativeMethod, useNativeApp } from '@/lib/native-app';
import { cssColorToRgb, stripeAppearance, type PayField, type PayPalette } from '@/lib/stripe-appearance';
import {
  amountMatches,
  applePayRecurring,
  cardFieldsOption,
  cardSaveOptions,
  elementsOptions,
  expressAvailable,
  expressAnyOn,
  expressButtonTheme,
  expressMethods,
  fitsPlan,
  payBody,
  parseCardSetup,
  payerBillingDetails,
  setupElementsOptions,
  stripeErrorText,
  stripeReturnUrl,
  subscriptionUrl,
  toMinorUnits,
  walletVia,
  type PayChoice,
  type PayerProfile,
  type PayMoney,
  type PayPeriod,
  type PayStep,
  type PayVia,
  type PayWallets,
} from '@/lib/pay-flow';
import { reportUiError } from '@/lib/ui-error-report';
import { DError } from './drawer-kit';
import { FCard, FField, FPane, KInput } from './form-kit';
import { LockIcon, postJson, useFmtMoney } from './billing-shared';
import { PayerSummary, usePayer } from './payer-sheet';
import { SettingsHero } from './settings-hero';
import { assetUrl } from '@/lib/asset-url';

// ─────────────────────────── Stripe.js — один на страницу ───────────────────────────

let stripeCache: { pk: string; p: Promise<Stripe | null> } | null = null;

/** Stripe.js грузится один раз (модульный синглтон): его зовёт уже ЭКРАН РАСЧЁТА (§1.4 п.3),
 *  слой и путь «привязанной картой» берут тот же экземпляр. В приложении — никогда (Э5.5). */
export function stripeFor(publishableKey: string): Promise<Stripe | null> | null {
  if (isNativeApp()) return null;
  if (!stripeCache || stripeCache.pk !== publishableKey) {
    const p = loadStripe(publishableKey);
    stripeCache = { pk: publishableKey, p };
    // Не загрузился — следующее открытие слоя пробует заново, а не берёт отклонённый промис.
    p.catch(() => {
      if (stripeCache?.p === p) stripeCache = null;
    });
  }
  return stripeCache.p;
}

/** Оплата привязанной картой Stripe (§2.3): формы нет, `confirmPayment` по `clientSecret` без
 *  elements, `redirect: 'if_required'`; 3-D Secure банка, если он попросит, откроется окном Stripe
 *  тут же, а переход (редко) вернёт на `returnUrl`. */
export async function confirmSavedStripeCard(
  publishableKey: string,
  clientSecret: string,
  returnUrl: string,
): Promise<{ ok: true } | { ok: false; error: { type?: string; message?: string; code?: string } | null }> {
  const p = stripeFor(publishableKey);
  const stripe = p ? await p.catch(() => null) : null;
  if (!stripe) return { ok: false, error: null };
  const { error } = await stripe.confirmPayment({
    clientSecret,
    confirmParams: { return_url: returnUrl },
    redirect: 'if_required',
  });
  return error ? { ok: false, error: { type: error.type, message: error.message, code: error.code } } : { ok: true };
}

/**
 * Ошибка Stripe → текст клиенту (Р2): своё слово Stripe — только у ошибок клиента (карта
 * отклонена, поле заполнено неверно); остальное — наш общий текст и запись в центр ошибок
 * (запрет №7: технические ошибки платформы клиенту не показываем, владельцу — в TG).
 */
export function stripeErrorForClient(
  err: { type?: string; message?: string; code?: string } | null | undefined,
  fallback: string,
  ctx: Record<string, unknown>,
): string {
  const r = stripeErrorText(err, fallback);
  if (r.report) {
    reportUiError('payment_stripe_client', `stripe: ${err?.message ?? 'нет ответа Stripe.js'}`, {
      ...ctx,
      type: err?.type ?? null,
      code: err?.code ?? null,
    });
  }
  return r.text;
}

/** Ошибка формы оплаты — с вибрацией, как ошибки форм кабинета (профиль, мастер). */
export function payErrorBuzz(): void {
  if (nativeMethod('haptic')) void nativeBridge()?.haptic?.({ kind: 'warning' });
  else if (typeof navigator !== 'undefined') navigator.vibrate?.(30);
}

// ─────────────────────────── чтение палитры и поля ───────────────────────────

/** Токены, которые получает форма Stripe — ровно эти имена из tokens.css. */
const PALETTE_TOKENS: Record<keyof PayPalette, string> = {
  bg: '--bg',
  ink: '--ink',
  accentSoft: '--accent-soft',
  accentStroke: '--accent-stroke',
  red: '--red',
  line: '--line',
};

/** Любой цвет CSS (oklch, color-mix…) → rgb()/rgba() через холст 1×1. */
function toRgb(v: string): string {
  const direct = cssColorToRgb(v);
  if (direct) return direct;
  const cv = document.createElement('canvas');
  cv.width = 1;
  cv.height = 1;
  const ctx = cv.getContext('2d', { willReadFrequently: true });
  if (!ctx) return v;
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillStyle = v;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
  return a === 255 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${Math.round(((a ?? 0) / 255) * 1000) / 1000})`;
}

function readPalette(): PayPalette {
  const cs = getComputedStyle(document.documentElement);
  return Object.fromEntries(
    Object.entries(PALETTE_TOKENS).map(([k, name]) => [k, toRgb(cs.getPropertyValue(name))]),
  ) as unknown as PayPalette;
}

const num = (v: string): number => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/** Вид поля — с настоящего поля формы набора в пузыре (`FField cap`: подпись `.kf-blk__cap`,
 *  пузырь `.kf-blk__body`, голый контрол внутри) — тот же рецепт, что у поля «Название» мастера
 *  шаблона, и в этом же листе (панель-телефон десктопа получает телефонные числа). */
function readField(fld: HTMLElement): PayField | null {
  const body = fld.querySelector<HTMLElement>('.kf-blk__body');
  const cap = fld.querySelector<HTMLElement>('.kf-blk__cap');
  const inp = fld.querySelector('input');
  if (!body || !cap || !inp) return null;
  const b = getComputedStyle(body);
  const i = getComputedStyle(inp);
  const l = getComputedStyle(cap);
  const fontSize = num(i.fontSize);
  return {
    height: body.getBoundingClientRect().height,
    radius: num(b.borderTopLeftRadius),
    fontSize,
    lineHeight: num(i.lineHeight) || Math.round(fontSize * 1.4 * 100) / 100,
    fontFamily: i.fontFamily,
    padX: num(b.paddingLeft),
    border: num(b.borderTopWidth),
    fill: toRgb(b.backgroundColor),
    placeholder: toRgb(getComputedStyle(inp, '::placeholder').color),
    labelSize: num(l.fontSize),
    labelWeight: num(l.fontWeight) || 400,
    labelColor: toRgb(l.color),
    labelGap: num(l.marginBottom),
  };
}

/** Высота кнопок кошельков — 44, рекомендация Apple для кнопок (владелец 08.10: было 52 по
 *  кнопке набора — крупнее стандарта, слой не помещался); Stripe принимает 40–55. */
const WALLET_BUTTON_H = 44;
function readButtonHeight(): number {
  return WALLET_BUTTON_H;
}

/** Тёмная тема кабинета сейчас (тема кнопок кошельков, §1.2 п.2): явная `data-theme` или
 *  системная, если явной нет. */
function readDark(): boolean {
  const th = document.documentElement.getAttribute('data-theme');
  if (th === 'dark') return true;
  if (th === 'light') return false;
  return !!window.matchMedia?.('(prefers-color-scheme: dark)')?.matches;
}

// ─────────────────────────── слой ───────────────────────────

/** Замок слоя на время платежа (Р1, §1.4 п.4): пока идёт `submit` → `POST` → `confirmPayment`
 *  (в т.ч. окно 3-D Secure банка) или лист кошелька, «‹», смахивание и Escape слой не снимают —
 *  `closeAsk` уровня читает этот флаг; второе нажатие «Оплатить» второго `POST` не даёт. */
export interface CardSheetLock {
  busy: () => boolean;
  set: (busy: boolean) => void;
}

/** Замок для одного открытия слоя: флаг живёт в замыкании (пропсы не мутируются). */
export function cardSheetLock(): CardSheetLock {
  let busy = false;
  return { busy: () => busy, set: (b) => void (busy = b) };
}

/** Тело `POST` из слоя: выбор клиента и способ (§3.3, договор §7.6); предмет добавляет хозяин. */
export type LayerPayBody = ReturnType<typeof payBody>;

/** Следующее списание (PAY-FLOW-TZ §2.3): что экран расчёта передаёт экрану оплаты — для Apple Pay
 *  (§3.6). Сумма — полная цена срока (в валюте платежа, не в центах), дата — `nextCharge`. */
export interface PayNext {
  amount: number;
  date: Date;
  period: PayPeriod;
  /** Число аккаунтов после оплаты — «Scriptera Pro — 2 аккаунта». */
  accounts: number;
}

export interface StripeCardSheetProps {
  /** Ключ Stripe.js (`pk_…`) из сводки — слой открывается без сервера (§2.1). */
  publishableKey: string;
  /** Сумма кнопки расчёта (в евро/долларах, не в центах) — то же число, что печатает кнопка. */
  amount: number;
  currency: Currency;
  /** Сумма на кнопке, уже с валютой («20 €»). */
  priceLabel: string;
  /** Тумблеры кошельков админки (`summary.payWallets`); автопродление применяет слой. */
  wallets: PayWallets;
  /** Автопродление, выбранное на экране подтверждения (§18.2): PayPal в ряду только без него, Apple Pay —
   *  повторяющийся платёж. Слой своего состояния автопродления не держит. */
  autorenew: boolean;
  /** «Сохранить карту», выбранное на экране подтверждения (§18.1 п.2): в форме переключателя нет — сохранение
   *  карты применяется к `Elements` перед `submit` (`applySave(autorenew || bindCard)`, §3.3 п.3). На PayPal и
   *  кошельки не влияет. Сохранённая карта выбирается тоже там — эта форма всегда для новой карты. */
  bindCard: boolean;
  /** Следующее списание — для Apple Pay при автопродлении (§3.6); null — не известно. */
  next: PayNext | null;
  /** Плательщик — пока кэш платёжных данных пуст (обычно он уже прогрет). */
  payer: PayerProfile | null;
  /** Общий с `closeAsk` уровня флаг «идёт платёж». */
  lock: CardSheetLock;
  /** `POST subscribe`/`account-add` с телом слоя → что делать (payStep). */
  run: (body: LayerPayBody) => Promise<PayStep>;
  /** Отказ ручки → текст у кнопки; null — хозяин забрал сам (подтверждение телефона). */
  errorText: (code: string) => string | null;
  /** Ответ не про Stripe (dev без кассы и т.п.) — хозяин доделывает сам. */
  onOther: (step: PayStep) => void;
  /** Stripe принял платёж (3-D Secure, если был, пройден) — хозяин снимает ВСЮ стопку оплаты до
   *  «Подписки» и показывает там ожидание этого платежа (PayFrame). */
  onSubmitted: (paymentId: string, wait: 'confirming' | 'charging') => void;
}

// INVARIANT(scriptera): СЛОЙ «ОПЛАТА» В iOS-ПРИЛОЖЕНИИ НЕ ОТКРЫВАЕТСЯ НИКОГДА — под UA
// `ScripteraApp` он ничего не рисует и Stripe.js не грузит, даже если его открыли (консоль,
// старый кэш страницы, чужая кнопка). Причина: в приложении ни цен, ни оплат (CLAUDE.md,
// позиционирование п.7) — Apple снимает приложение за оплату мимо своей. Проверка
// синхронная (`isNativeApp`), а не только хук: хук становится true после первого кадра.
// Канон: docs/MULTICURRENCY-PLAN.md Э5.5; юнит — stripe-card-sheet.test.tsx.
export function StripeCardSheet(props: StripeCardSheetProps) {
  const native = useNativeApp();
  if (native || isNativeApp()) return null;
  return <CardSheetBody {...props} />;
}

/** Предел ожидания формы карты: не пришла — на месте плашек текст ошибки (ревизия клиента п.1). */
export const LOAD_MAX_MS = 15_000;
/** Сколько ждать ответа ряда кошельков после готовности формы (§1.4 п.2): не ответил — «кнопок
 *  нет», ряд и разделитель убираются тем же кадром, что встаёт форма. */
export const EXPRESS_WAIT_MS = 6000;
/** «Готово» на кнопке перед закрытием слоя (§1.4 п.4). */
export const DONE_MS = 300;
/** Сколько ждать, пока невидимые форма и ряд дорастут до высоты плашек после ответа Stripe. Пока они растут,
 *  весь слой под замком (§14.4, приёмка 09.10) — торопиться некуда: прежние 2,5 с под Slow 3G подменяли
 *  недоросшую форму (190 из 268 px), и она дорастала на виду, сдвигая «Плательщика» и «Оплатить». Предел — тот
 *  же, что у загрузки формы. */
export const SIZE_MAX_MS = LOAD_MAX_MS;
/** Форма и ряд не меняли размер столько — подмена как есть (владелец 09.10: «очень долго ждал форму» —
 *  ожидание совпадения высот с плашками доходило до SIZE_MAX_MS; под замком плашек не видно, совпадать незачем).
 *  Секунда, не меньше: форма Stripe дорастает шагами с паузами в 300–400 мс (Chromium под нагрузкой), и ранняя
 *  подмена показывала рост на виду (сдвиг плательщика и кнопки — экранный тест 09.10). */
export const SETTLE_MS = 1000;

/**
 * «СРОК» И «КОД» STRIPE — В СТРОКУ ИЛИ СТОЛБИКОМ: Stripe кладёт их столбиком, когда подпись не влезает в
 * половину строки С ЗАПАСОМ. Промер 08.10 (Chromium, подпись «Окончание срока действия» 178 px, Onest 13):
 * половина 187 и меньше — столбиком, 191 и больше — в строку; запас между 9 и 13 — берём середину. Без запаса
 * заглушка на 402 стояла в строку, а форма вставала столбиком — подмена сдвигала всё ниже на 94 px (§14.4).
 */
const STRIPE_PAIR_SLACK = 11;

/** Ширина ТЕКСТА подписи (не коробки: у подписи плашки `nowrap` и обрезка — scrollWidth равен коробке). */
function labelTextWidth(l: HTMLElement): number {
  const r = document.createRange();
  r.selectNodeContents(l);
  // Окружение без раскладки (jsdom) — по коробке.
  return typeof r.getBoundingClientRect === 'function' ? r.getBoundingClientRect().width : l.scrollWidth;
}

/** Уступки вместимости, применённые при открытии (§1.3): 0 — ни одной, 1 — строка защиты
 *  короткая, 2 — + сводка в одну строку, 3 — + отступы по минимуму, 4 — + подписи формы мельче. */
type FitSteps = 0 | 1 | 2 | 3 | 4;

/** Поля шага 4 (§1.3 п.4): подписи Stripe компактнее. */
const LABEL_COMPACT = { fontSize: '12px', marginBottom: '4px' };


/**
 * ВИД ФОРМЫ STRIPE И ЗАГРУЗКА STRIPE.JS — общие для слоя «Оплата» и слоя «Добавить карту» (§6.5):
 * Stripe.js один на страницу, его отказ — ошибка на месте формы и запись в центр ошибок; палитра и
 * поле снимаются с эталона (`StripeProbe`) при открытии и при смене темы/ширины.
 */
function useStripeLook(publishableKey: string, lock: CardSheetLock) {
  const props = { lock };
  const probe = useRef<HTMLDivElement | null>(null);
  const [field, setField] = useState<PayField | null>(null);
  const [palette, setPalette] = useState<{ key: string; value: PayPalette } | null>(null);
  const [stripeP] = useState(() => stripeFor(publishableKey));
  // Stripe.js не загрузился (сеть, блокировщик) — на месте плашек общий текст ошибки и запись в
  // центр ошибок, «Оплатить» погашена (ревизия клиента, п.1).
  const [loadFailed, setLoadFailed] = useState(false);
  const failLoad = useCallback((message: string) => {
    setLoadFailed((was) => {
      if (!was) {
        reportUiError('payment_stripe_client', `stripe: ${message}`, { step: 'load' });
        payErrorBuzz();
      }
      return true;
    });
  }, []);
  useLayoutEffect(() => {
    if (!stripeP) return;
    let alive = true;
    stripeP.then(
      (s) => {
        if (alive && !s) failLoad('loadStripe вернул null');
      },
      (e: unknown) => {
        if (alive) failLoad(`loadStripe: ${e instanceof Error ? e.message : String(e)}`);
      },
    );
    return () => {
      alive = false;
    };
  }, [stripeP, failLoad]);
  const [buttonHeight] = useState(() => readButtonHeight());
  const [dark, setDark] = useState(() => readDark());

  // Значения палитры и поля — при открытии и при смене темы/ширины. Форма НЕ пересоздаётся
  // (Р6): новый вид уходит в `elements.update({ appearance })` (react-stripe-js делает это сам
  // при смене options.appearance). Пока идёт платёж — вид не трогаем вовсе.
  const measure = useCallback(() => {
    if (props.lock.busy()) return;
    const fld = probe.current;
    if (!fld) return;
    const f = readField(fld);
    if (!f) return;
    const p = readPalette();
    const key = JSON.stringify([p, f]);
    setField((cur) => (cur && JSON.stringify(cur) === JSON.stringify(f) ? cur : f));
    setPalette((cur) => (cur?.key === key ? cur : { key, value: p }));
    setDark(readDark());
  }, [props.lock]);

  useLayoutEffect(() => {
    measure();
    const mo = new MutationObserver(() => measure());
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'data-tint', 'data-skin', 'class'],
    });
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onMq = () => measure();
    mq.addEventListener('change', onMq);
    // Ширина листа меняет числа поля (телефон / широкий) — тоже пересчёт.
    const ro = new ResizeObserver(() => measure());
    if (probe.current) ro.observe(probe.current);
    return () => {
      mo.disconnect();
      mq.removeEventListener('change', onMq);
      ro.disconnect();
    };
  }, [measure]);
  return { probe, field, palette, stripeP, loadFailed, buttonHeight, dark };
}

/** Эталон поля — поле «Название» мастера шаблона (канон: эталон вида формы — мастер): тот же
 *  контекст правил, что у поля рендерера в мастере (`FPane wiz` → `FCard` → поле `pfField` с
 *  пузырём `.kf-blk__body`), невидимое и вне потока. С него снимаются высота, радиус, поля, шрифт и
 *  фон пузыря, подпись — числа живут в kit.css/cabinet.css. Та же высота — у плашек-скелетов. */
function StripeProbe({ probe }: { probe: RefObject<HTMLDivElement | null> }) {
  return (
    <div ref={probe} aria-hidden style={{ position: 'absolute', insetInline: 0, visibility: 'hidden', pointerEvents: 'none' }}>
      <FPane wiz>
        <FCard>
          <FField cap label="0" flush className="pfField">
            <KInput tabIndex={-1} readOnly value="0" />
          </FField>
        </FCard>
      </FPane>
    </div>
  );
}

function CardSheetBody(props: StripeCardSheetProps) {
  const { publishableKey, amount, currency, autorenew, bindCard } = props;
  const fmtMoney = useFmtMoney();
  // Кошельки PI (ответ ручки `stripe.wallets`) поправляют кэш сводки, если разошлись (ревизия п.4):
  // админ переключил PayPal или Stripe снял его повтором, пока слой был открыт.
  const [walletsFix, setWalletsFix] = useState<PayWallets | null>(null);
  const wallets = walletsFix ?? props.wallets;
  // Сумма платежа из ответа ручки, если разошлась с расчётом (§2.2 п.4, ревизия п.1–2): подпись
  // кнопки показывает её — той же функцией, что печатает сумму на экране расчёта.
  const [moneyFix, setMoneyFix] = useState<PayMoney | null>(null);
  const priceLabel = moneyFix ? fmtMoney(moneyFix.amount, moneyFix.currency) : props.priceLabel;
  const locale = useLocale();
  const { probe, field, palette, stripeP, loadFailed, buttonHeight, dark } = useStripeLook(publishableKey, props.lock);

  // `Elements` — ОДИН РАЗ при открытии (§3.3 п.1): способы и сохранение по автопродлению экрана
  // расчёта. Меняют их только служебные сверки с ответом ручки (сумма, PayPal снят админом).
  const [amountMinor, setAmountMinor] = useState(() => toMinorUnits(amount, currency));
  const paypalOn = wallets.paypal;
  const payOpts = useMemo(() => {
    const o = elementsOptions(amount, currency, { ...wallets, paypal: paypalOn }, autorenew);
    return { ...o, amount: amountMinor };
    // wallets — только ради PayPal (единственное, что сверка может поменять).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, currency, paypalOn, autorenew, amountMinor]);

  // ВМЕСТИМОСТЬ (§1.3): уступки считаются ОДИН раз при открытии по измеренной высоте слоя.
  const [fit, setFit] = useState<FitSteps | null>(null);

  const appearance = useMemo<Appearance | null>(() => {
    if (!field || !palette) return null;
    const a = stripeAppearance(palette.value, field, { walletHeight: buttonHeight });
    if ((fit ?? 0) < 4) return a;
    const rules = a.rules ?? {};
    return { ...a, rules: { ...rules, '.Label': { ...(rules['.Label'] ?? {}), ...LABEL_COMPACT } } };
  }, [field, palette, fit, buttonHeight]);

  const options = useMemo(
    () =>
      appearance
        ? {
            ...payOpts,
            appearance,
            // Язык формы, кнопок кошельков и текстов ошибок = язык кабинета (§4 PAY-SCREEN-TZ).
            locale: (locale === 'en' ? 'en' : 'ru') as 'en' | 'ru',
            // Наш шрифт в iframe Stripe: CSS с @font-face Onest (public/fonts/onest.css, отдаётся
            // с Access-Control-Allow-Origin: * — next.config.mjs).
            fonts: [{ cssSrc: `${window.location.origin}${assetUrl('/fonts/onest.css')}` }],
          }
        : null,
    [appearance, payOpts, locale],
  );

  const shared: LayoutShared = {
    ...props,
    buttonHeight,
    dark,
    field,
    fit: fit ?? 0,
    onFit: (steps) => setFit((cur) => cur ?? steps),
    onRefit: (steps) => setFit(steps),
    // Выбор клиента сделан на экране подтверждения (§18.2): новая карта, автопродление и «Сохранить карту».
    choice: { saved: false, autorenew, bindCard },
    wallets,
    priceLabel,
    elementsAmount: { amount: amountMinor, currency: payOpts.currency },
    elementsTypes: payOpts.allowedPaymentMethodTypes,
    onAmountResync: (money) => {
      setAmountMinor(toMinorUnits(money.amount, money.currency));
      setMoneyFix(money);
    },
    onWalletsResync: (paypal) => setWalletsFix({ ...wallets, paypal }),
  };

  return (
    <FPane>
      <StripeProbe probe={probe} />
      {!loadFailed && options && stripeP ? (
        // Ключ — только язык (Р7, §1.4 п.7): смена языка при открытом слое пересобирает форму на
        // новом языке (locale Elements обновлением не меняется) — на это время снова плашки.
        // Тема, ширина, тумблеры, сумма — без пересборки (`elements.update`).
        <Elements key={locale} stripe={stripeP} options={options}>
          <PayForm {...shared} />
        </Elements>
      ) : (
        <PayLayout {...shared} loadFailed={loadFailed} pay={null} />
      )}
    </FPane>
  );
}

/** Общее для слоя с формой и без неё (Stripe.js не загрузился, вид ещё не снят). */
interface LayoutShared extends StripeCardSheetProps {
  buttonHeight: number;
  dark: boolean;
  field: PayField | null;
  fit: FitSteps;
  onFit: (steps: FitSteps) => void;
  /** Пересчёт уступок один раз — по фактическим высотам формы и ряда в момент подмены (ревизия п.6). */
  onRefit: (steps: FitSteps) => void;
  /** Выбор клиента для тела `POST` (с экрана подтверждения; у кошелька `bindCard` снимается). */
  choice: PayChoice;
  elementsAmount: { amount: number; currency: string };
  /** Способы `Elements` сейчас — для сверки с PI ответа ручки (ревизия п.4). */
  elementsTypes: string[];
  /** Сумма ответа ручки разошлась с расчётом: новая сумма `Elements` и подпись кнопки. */
  onAmountResync: (money: PayMoney) => void;
  /** PayPal у PI ответа ручки не тот, что в `Elements`: способы и ряд — по ответу. */
  onWalletsResync: (paypal: boolean) => void;
}

/** Состояние кнопки «Оплатить» (§1.4 п.4). */
type CtaState = 'idle' | 'busy' | 'done';

/** Что даёт форма Stripe раскладке: живые элементы, их готовность и обработчики. */
interface PayParts {
  express: ReactNode | null;
  expressState: 'wait' | 'yes' | 'no';
  form: ReactNode;
  formReady: boolean;
  formFailed: boolean;
  cta: CtaState;
  err: string | null;
  canPay: boolean;
  onPay: () => void;
}

/**
 * ФОРМА СЛОЯ под `Elements`. Ряд кошельков и форма карты смонтированы сразу под плашками-скелетами
 * (§1.4 п.2): невидимые и вне потока, пока Stripe их рисует; по `ready` формы и ответу ряда (или
 * через EXPRESS_WAIT_MS) — подмена одним кадром, без размонтирования. «Оплатить» — §2.2/§2.3,
 * кошелёк — §2.4; один `POST` на нажатие (замок `lock`).
 */
function PayForm(p: LayoutShared) {
  const t = useTranslations('cabinet.billing');
  const locale = useLocale();
  const stripe = useStripe();
  const elements = useElements();
  const { payer: cached } = usePayer();
  const payer = cached ?? p.payer;
  // Поля и умолчания формы задаются один раз при монтировании (Stripe их обновлением не меняет).
  const [peOptions] = useState(() => ({
    layout: 'tabs' as const,
    paymentMethodOrder: ['card'],
    // Кошельки живут в ряду выше — внутри карты их нет; Link убран совсем (§0 п.3).
    wallets: { applePay: 'never' as const, googlePay: 'never' as const, link: 'never' as const },
    // Имя, почта, телефон, страна — из платёжных данных (сводка ниже), не полями: в форме только
    // номер, срок, код (ревизия п.4).
    fields: { billingDetails: cardFieldsOption(payer) },
    defaultValues: {
      billingDetails: {
        name: payer?.name ?? '',
        email: payer?.email ?? '',
        phone: payer?.phone ?? '',
        address: { country: payer?.country ?? '' },
      },
    },
    terms: { card: 'never' as const, paypal: 'never' as const },
  }));
  const [formReady, setFormReady] = useState(false);
  const [formFailed, setFormFailed] = useState(false);
  // Ряд кошельков: 'wait' — Stripe ещё не ответил; 'yes' — есть хоть одна кнопка; 'no' — нет.
  const [express, setExpress] = useState<'wait' | 'yes' | 'no'>('wait');
  const [cta, setCta] = useState<CtaState>('idle');
  const [err, setErr] = useState<string | null>(null);

  const autorenew = p.choice.autorenew === true;
  // Кнопки ряда — один раз по автопродлению экрана расчёта (§3.1: PayPal только в виде Б); ряд НЕ
  // пересоздаётся (§3.3 п.6). PayPal может снять только служебная сверка с ответом ручки.
  const methods = useMemo(() => expressMethods(p.wallets, autorenew), [p.wallets, autorenew]);
  const anyOn = expressAnyOn(methods);
  const rowState: 'wait' | 'yes' | 'no' = !anyOn ? 'no' : express;
  // Ряд, однажды убранный, задним числом не появляется (сдвиг под пальцем). Ответ — в память устройства.
  const setRow = useCallback((next: 'yes' | 'no') => setExpress((cur) => (cur === 'no' ? cur : next)), []);
  // СОХРАНЕНИЕ КАРТЫ У `Elements` СЕЙЧАС (§3.3 п.3–4): при открытии — по автопродлению; меняется
  // только `update` перед `submit` (галочка) и сбросом в `onClick` кошелька (вид Б). Ref, не
  // состояние: экран от него не зависит.
  // `paymentMethodOptions` у `elements.update` — в документации Stripe (docs.stripe.com/js/elements_object/
  // update), но не в типах stripe-js 10; с Stripe.js «dahlia» `update` отдаёт промис — ждём его.
  const elSave = useRef(autorenew);
  const applySave = (want: boolean): Promise<unknown> => {
    if (!elements || elSave.current === want) return Promise.resolve();
    elSave.current = want;
    const update = elements.update as unknown as (o: { paymentMethodOptions: ReturnType<typeof cardSaveOptions> }) => unknown;
    return Promise.resolve(update.call(elements, { paymentMethodOptions: cardSaveOptions(want) }));
  };

  // Предел ожидания формы (ревизия клиента п.1): не пришла за LOAD_MAX_MS — ошибка на её месте.
  useLayoutEffect(() => {
    if (formReady || formFailed) return;
    const id = window.setTimeout(() => {
      setFormFailed(true);
      reportUiError('payment_stripe_client', `stripe: форма не загрузилась за ${LOAD_MAX_MS} мс`, { step: 'load' });
      payErrorBuzz();
    }, LOAD_MAX_MS);
    return () => window.clearTimeout(id);
  }, [formReady, formFailed]);
  // Ряд не ответил за EXPRESS_WAIT_MS после готовности формы — «кнопок нет» (§1.4 п.2).
  useLayoutEffect(() => {
    if (!(formReady || formFailed) || rowState !== 'wait') return;
    const id = window.setTimeout(() => setRow('no'), EXPRESS_WAIT_MS);
    return () => window.clearTimeout(id);
  }, [formReady, formFailed, rowState, setRow]);

  const fail = (text: string) => {
    setErr(text);
    payErrorBuzz();
  };
  // Ошибка карты и неверное поле Stripe сам пишет у поля (§1.4 п.4) — второй раз над кнопкой не
  // повторяем, только вибрация; прочее — наш текст над кнопкой.
  const stripeFail = (e: { type?: string; message?: string; code?: string } | null, step: string, paymentId?: string) => {
    const text = stripeErrorForClient(e, t('errGeneric'), { paymentId: paymentId ?? null, step });
    if (e?.type === 'card_error' || e?.type === 'validation_error') payErrorBuzz();
    else fail(text);
  };

  /** `POST` и разбор: Stripe-шаг или null (ошибка показана, хозяин забрал, иной исход). */
  const post = async (via: PayVia): Promise<Extract<PayStep, { kind: 'stripe-card' | 'stripe-saved' }> | null> => {
    // Сохранённой — `saved`; форма — с галочкой; кошелёк карту не сохраняет в виде Б (§3.3 п.4),
    // в виде А сохраняет (автопродление, сервер включает это сам).
    const choice: PayChoice = via === 'card' ? p.choice : { ...p.choice, bindCard: false };
    const step = await p.run(payBody(choice, via));
    if (step.kind === 'stripe-card' || step.kind === 'stripe-saved') return step;
    if (step.kind === 'error') {
      const text = p.errorText(step.error);
      if (text !== null) fail(text);
      return null;
    }
    p.onOther(step);
    return null;
  };

  const finish = (paymentId: string, wait: 'confirming' | 'charging') => {
    setCta('done');
    window.setTimeout(() => {
      p.lock.set(false);
      p.onSubmitted(paymentId, wait);
    }, DONE_MS);
  };

  /**
   * СПОСОБЫ ELEMENTS = СПОСОБЫ PI (ревизия п.4): `Elements` открыт по кэшу сводки, а PI создан по
   * тумблерам админки СЕЙЧАС (и повтором без способа, выключенного в Stripe). Разошлись по PayPal —
   * `elements.update({ allowedPaymentMethodTypes })` по ответу и ряд пересоздаётся (кнопки PayPal
   * нет/есть) до подтверждения. Ответ — есть ли PayPal у PI.
   */
  const syncTypes = (step: Extract<PayStep, { kind: 'stripe-card' }>): boolean => {
    const paypal = step.wallets.paypal;
    if (p.elementsTypes.includes('paypal') !== paypal) {
      reportUiError('pay', 'types_resync', { paymentId: step.paymentId, elements: p.elementsTypes.join(','), paypal });
      elements?.update({ allowedPaymentMethodTypes: ['card', ...(paypal ? ['paypal'] : [])] });
      p.onWalletsResync(paypal);
    }
    return paypal;
  };

  /** Статус PI после `confirmPayment`: принят — ждём вебхук на «Подписке»; иначе — ошибка. */
  const accepted = (status: string | undefined): boolean =>
    status === undefined || status === 'succeeded' || status === 'processing' || status === 'requires_capture';

  /** «Оплатить» (§2.2 картой, §2.3 привязанной). Один `POST` на нажатие: под замком. */
  const onPay = async () => {
    if (p.lock.busy() || !stripe || !elements || !payer) return;
    p.lock.set(true);
    setCta('busy');
    setErr(null);
    let ok = false;
    try {
      let step: Extract<PayStep, { kind: 'stripe-card' }> | null = null;
      // Сверка суммы (§2.2 п.4): расходится — `elements.update({ amount })` и повтор ОДИН раз.
      // Сумма Elements в этом нажатии: после пересчёта — новая (пропсы обновятся только со следующим
      // рендером, а повтор идёт в том же обработчике).
      let elAmount = p.elementsAmount;
      // Галочка «Сохранить карту» (вид Б) — в `Elements` только сейчас, перед `submit` (§3.3 п.3);
      // сняли после неудачной попытки — обратное `update`. В виде А сохранение стоит с открытия.
      await applySave(autorenew || p.choice.bindCard);
      for (let attempt = 0; attempt < 2 && !step; attempt += 1) {
        const sub = await elements.submit();
        // Пустое или неверное поле — Stripe подсветил его сам; `POST` не идёт.
        if (sub.error) return;
        const got = await post('card');
        if (!got) return;
        if (got.kind !== 'stripe-card') return fail(t('errGeneric'));
        if (amountMatches(got.money, elAmount)) {
          step = got;
          break;
        }
        reportUiError('pay', 'amount_resync', {
          paymentId: got.paymentId,
          elements: elAmount.amount,
          server: got.money ? toMinorUnits(got.money.amount, got.money.currency) : null,
        });
        if (attempt === 1 || !got.money) return fail(t('errGeneric'));
        // Новая сумма — в Elements и на кнопку (ревизия п.2): подтверждается уже она.
        elAmount = { amount: toMinorUnits(got.money.amount, got.money.currency), currency: elAmount.currency };
        elements.update({ amount: elAmount.amount });
        p.onAmountResync(got.money);
      }
      if (!step) return;
      syncTypes(step);
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: step.clientSecret,
        confirmParams: {
          return_url: stripeReturnUrl(window.location.origin, locale, step.paymentId),
          // Форма не спрашивает ни имени, ни почты, ни страны — всё из платёжных данных кабинета,
          // без пустых полей (ревизия п.4).
          payment_method_data: { billing_details: payerBillingDetails(payer) },
        },
        redirect: 'if_required',
      });
      if (error) return stripeFail(error, 'confirmPayment', step.paymentId);
      if (!accepted(paymentIntent?.status)) return fail(t('errGeneric'));
      ok = true;
      finish(step.paymentId, 'confirming');
    } catch (e) {
      stripeFail({ message: e instanceof Error ? e.message : String(e) }, 'pay');
    } finally {
      if (!ok) {
        p.lock.set(false);
        setCta('idle');
      }
    }
  };

  /** Кошелёк (§2.4): лист Stripe уже показал сумму и способ; `POST` с текущими тумблерами. */
  const onWalletConfirm = async (e: {
    expressPaymentType: string;
    paymentFailed: (o?: { reason?: 'fail' | 'invalid_shipping_address' }) => void;
  }) => {
    const via = walletVia(e.expressPaymentType);
    if (!via || p.lock.busy() || !stripe || !elements) {
      e.paymentFailed({ reason: 'fail' });
      return;
    }
    p.lock.set(true);
    setErr(null);
    let ok = false;
    try {
      const sub = await elements.submit();
      if (sub.error) return stripeFail(sub.error, `wallet:${via}`);
      const step = await post(via);
      if (!step) return;
      if (step.kind !== 'stripe-card') return fail(t('errGeneric'));
      // PayPal снят сервером (тумблер админки, повтор без способа) — подтверждать нечем (ревизия п.4).
      if (!syncTypes(step) && via === 'wallet:paypal') return fail(t('errGeneric'));
      if (!amountMatches(step.money, p.elementsAmount)) {
        // Лист кошелька уже показал прежнюю сумму — подтверждать другую нельзя: сумма
        // обновляется, платёж отклоняется, клиент нажимает снова (РЕШЕНИЕ НЕ В ТЗ, ближайшее §2.2 п.4).
        reportUiError('pay', 'amount_resync', { paymentId: step.paymentId, via });
        if (step.money) {
          elements.update({ amount: toMinorUnits(step.money.amount, step.money.currency) });
          p.onAmountResync(step.money);
        }
        return fail(t('errGeneric'));
      }
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: step.clientSecret,
        confirmParams: { return_url: stripeReturnUrl(window.location.origin, locale, step.paymentId) },
        redirect: 'if_required',
      });
      if (error) return stripeFail(error, `wallet:${via}`, step.paymentId);
      if (!accepted(paymentIntent?.status)) return fail(t('errGeneric'));
      ok = true;
      finish(step.paymentId, 'confirming');
    } catch (x) {
      stripeFail({ message: x instanceof Error ? x.message : String(x) }, `wallet:${via}`);
    } finally {
      if (!ok) {
        p.lock.set(false);
        // Отказ промиса `confirm` — Stripe закрывает лист сам.
        e.paymentFailed({ reason: 'fail' });
      }
    }
  };

  /**
   * Нажатие кнопки ряда (до листа кошелька, ≤ 1 с): вид Б — кошелёк карту не сохраняет, и если
   * `Elements` к этому моменту с сохранением (после неудачной попытки картой с галочкой) —
   * `update` обратно (§3.3 п.4). Вид А и Apple Pay — повторяющийся платёж в окне (§3.6).
   */
  const onWalletClick = (ev: StripeExpressCheckoutElementClickEvent) => {
    if (p.lock.busy() || !payer) return ev.reject();
    if (!autorenew) void applySave(false);
    if (autorenew && ev.expressPaymentType === 'apple_pay' && p.next) {
      return ev.resolve({
        applePay: applePayRecurring({
          description: t('applePayDesc', { n: p.next.accounts }),
          managementURL: subscriptionUrl(window.location.origin, locale),
          // Строка автопродления в окне Apple Pay подписана «Автопродление Pro» — чтобы не путалась с
          // «Pay Scriptera 288 €» (списание сейчас; владелец 09.10: «я плачу сейчас или через год?»).
          label: t('applePayRenewLabel'),
          amount: p.next.amount,
          currency: p.currency,
          period: p.next.period,
          start: p.next.date,
        }),
      });
    }
    // Ничего не собираем (ни адреса, ни почты): лист открывается сразу.
    ev.resolve({});
  };

  const expressNode = anyOn ? (
    <ExpressCheckoutElement
      options={{
        buttonHeight: p.buttonHeight,
        buttonType: { applePay: 'plain', googlePay: 'plain', paypal: 'paypal' },
        buttonTheme: expressButtonTheme(p.dark),
        paymentMethods: methods,
        // ВСЕ кнопки в одну строку, одинаковой ширины (§0 п.2, §1.2 п.2): на устройстве кнопок
        // не больше двух (Apple Pay ИЛИ Google Pay, плюс PayPal).
        // `overflow: 'never'` Stripe.js принимает только при maxRows 0 (IntegrationError, промер
        // 08.10) — 'auto': кнопок не больше двух при двух колонках, меню «ещё» не появляется.
        layout: { maxColumns: 2, maxRows: 1, overflow: 'auto' },
      }}
      onReady={(ev) => setRow(expressAvailable(ev.availablePaymentMethods) ? 'yes' : 'no')}
      onAvailablePaymentMethodsChange={(ev) => setRow(expressAvailable(ev.paymentMethods) ? 'yes' : 'no')}
      onClick={onWalletClick}
      onConfirm={(ev) => void onWalletConfirm(ev)}
      onLoadError={() => setRow('no')}
    />
  ) : null;

  const formNode = (
    <PaymentElement
      options={peOptions}
      onReady={() => setFormReady(true)}
      onLoadError={(ev) => {
        // Текст ошибки — ОДИН, на месте плашек формы (§1.4 п.3; ревизия п.5: был ещё и над
        // сводкой). Здесь — только запись в центр ошибок и вибрация.
        setFormFailed(true);
        stripeErrorForClient(ev.error, t('errGeneric'), { paymentId: null, step: 'load' });
        payErrorBuzz();
      }}
    />
  );

  return (
    <PayLayout
      {...p}
      payer={payer}
      loadFailed={false}
      pay={{
        express: expressNode,
        expressState: rowState,
        form: formNode,
        formReady,
        formFailed,
        cta,
        err,
        canPay: !!stripe && !!elements && !!payer && formReady && !formFailed,
        onPay: () => void onPay(),
      }}
    />
  );
}

/**
 * РАСКЛАДКА СЛОЯ — одна для формы и для её отсутствия (сверху вниз по §1.2). Пока форма и ряд не
 * пришли — плашки-скелеты их высоты, живые элементы под ними невидимы и вне потока; подмена —
 * одним кадром. Вместимость (§1.3) меряется здесь же один раз при открытии.
 */
function PayLayout(p: LayoutShared & { loadFailed: boolean; pay: PayParts | null }) {
  const t = useTranslations('cabinet.billing');
  const root = useRef<HTMLDivElement | null>(null);
  const pay = p.pay;
  const formSettled = !!pay && (pay.formReady || pay.formFailed);
  // Подмена ряда и формы — одним кадром: оба ответили (или ряд досрочно «нет»).
  // Готовность Stripe ≠ конечная высота (промер 08.10: форма отвечает `ready` на 198 px и дорастает
  // до 276 ещё ~0,2 с, ряд — до высоты кнопки): подмена ждёт, пока невидимые форма и ряд наберут
  // высоту своих плашек (ResizeObserver); не набрали за SIZE_MAX_MS — подмена как есть.
  const skelRef = useRef<HTMLDivElement | null>(null);
  const formPendRef = useRef<HTMLDivElement | null>(null);
  const rowPendRef = useRef<HTMLDivElement | null>(null);
  const fieldH = Math.round(p.field?.height ?? 52);
  // Подпись поля Stripe: кегль × 1,15 (промер iframe 08.10: 13 → 14,94) и labelSpacing до поля.
  const labelH = (p.field?.labelSize ?? 13) * 1.15 + (p.field?.labelGap ?? 8);
  // То же с уступкой 4 (подпись 12, до поля 4) — как плашки и форма сейчас.
  const labelLineNow = p.fit >= 4 ? 12 * 1.15 + 4 : labelH;
  const fitDone = useRef(false);
  const fitData = useRef<{ savings: number[] } | null>(null);
  /** Высота содержимого слоя и видимая высота его прокрутки (`.dr-body` стопки). По offsetTop,
   *  не по getBoundingClientRect: лист въезжает с масштабом. scrollHeight не годится — он не
   *  меньше видимой высоты. */
  const measureContent = (el: HTMLElement): { contentH: number; avail: number } | null => {
    let scroller: HTMLElement | null = el.parentElement;
    while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;
    if (!scroller) return null;
    const pane = el.parentElement ?? el;
    let top = 0;
    let e: HTMLElement | null = pane;
    while (e && e !== scroller && scroller.contains(e)) {
      top += e.offsetTop;
      e = e.offsetParent as HTMLElement | null;
    }
    if (e !== scroller) top -= scroller.offsetTop;
    return { contentH: top + pane.offsetHeight + num(getComputedStyle(scroller).paddingBottom), avail: scroller.clientHeight };
  };
  /**
   * Уступки по фактическим высотам в момент подмены: высота содержимого сейчас (с плашками и уже
   * применёнными уступками) + разница «форма Stripe − плашки формы» (при привязанной карте — вся
   * форма и строка «Сохранить карту», что вернёт «Другой картой») − ряд с разделителем, если он
   * уходит + сбережения уже применённых уступок = «без уступок»; дальше то же правило fitsPlan.
   */
  /**
   * Сколько даёт уступка 4 (подписи формы 12 / до поля 4) — с учётом того, что Stripe при более
   * мелкой подписи может положить «Срок» и «Код» в строку (промер 08.10, WebKit 402: при 12 — в
   * строку, при 13 — столбиком): тогда уходит целый ряд поля. Раскладку Stripe предсказываем его
   * же правилом — влезают ли подписи в половину строки; ширина подписи — `measureText` шрифтом
   * подписи (Onest, тот же, что получает iframe Stripe).
   */
  const step4Saving = (formWidth: number): number => {
    const size13 = p.field?.labelSize ?? 13;
    const gap13 = p.field?.labelGap ?? 8;
    const half = (formWidth - 12) / 2;
    const ctx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
    const family = p.field?.fontFamily ?? 'sans-serif';
    const widest = (size: number): number => {
      if (!ctx) return 0;
      ctx.font = `${p.field?.labelWeight ?? 400} ${size}px ${family}`;
      return Math.max(ctx.measureText(t('payCardExpiry')).width, ctx.measureText(t('payCardCvc')).width);
    };
    const formAt = (size: number, gap: number): number => {
      const pair = size * 1.15 + gap + fieldH;
      return widest(size) > half - STRIPE_PAIR_SLACK ? 3 * pair + 2 * 12.75 : 2 * pair + 12.75;
    };
    return Math.max(0, formAt(size13, gap13) - formAt(12, 4));
  };
  const refit = (): FitSteps | null => {
    const el = root.current;
    const data = fitData.current;
    if (!el || !data) return null;
    const m = measureContent(el);
    if (!m) return null;
    const h = (x: Element | null | undefined) => x?.getBoundingClientRect().height ?? 0;
    const formReal = h(formPendRef.current);
    let adj = 0;
    if (pay?.formReady && formReal > 0) adj += formReal - h(skelRef.current);
    const rowEl = el.querySelector<HTMLElement>(':scope > .pay-row');
    const orEl = el.querySelector<HTMLElement>(':scope > .pay-or');
    if (pay?.expressState === 'no' && rowEl && orEl?.nextElementSibling) {
      adj -= (orEl.nextElementSibling as HTMLElement).offsetTop - rowEl.offsetTop;
    }
    const formBox = el.querySelector<HTMLElement>(':scope > .pay-form');
    const savings = [...data.savings.slice(0, 3), formBox ? step4Saving(formBox.offsetWidth) : (data.savings[3] ?? 0)];
    const applied = savings.slice(0, p.fit).reduce((a, b) => a + b, 0);
    const need = Math.round(m.contentH + adj + applied);
    const plan = fitsPlan(m.avail, need, savings);
    el.dataset.payRefit = JSON.stringify({ avail: m.avail, need, from: p.fit, ...plan });
    return plan.steps as FitSteps;
  };
  // «СРОК» И «КОД» В СТРОКУ ИЛИ СТОЛБИКОМ — как их положит Stripe. Его правило — влезают ли подписи
  // «Окончание срока действия» / «Код безопасности» в половину строки (промер 08.10: WebKit на 402
  // при подписи 12 — в строку, при 13 — столбиком; панель 460 — в строку). Плашки меряют те же
  // подписи тем же шрифтом (Onest в iframe Stripe — /fonts/onest.css) и повторяют раскладку, иначе
  // подмена сдвинет слой. Подстраховка — ниже, в подмене: если форма Stripe всё же легла иначе,
  // плашки перекладываются один раз до подмены (а не форма «прыгает» на виду).
  const pairRef = useRef<HTMLDivElement | null>(null);
  // null — ещё не мерили (замер вместимости ждёт его: раскладка плашек меняет высоту формы).
  const [pairStack, setPairStack] = useState<boolean | null>(null);
  const pairFlipped = useRef(false);
  useLayoutEffect(() => {
    if (pairFlipped.current) return;
    const el = pairRef.current;
    if (!el) {
      setPairStack((cur) => cur ?? true);
      return;
    }
    // offsetWidth, не getBoundingClientRect: лист въезжает с масштабом, а нужна ширина раскладки.
    const half = (el.offsetWidth - 12) / 2;
    const labels = Array.from(el.querySelectorAll<HTMLElement>('.k-skel-field__label'));
    setPairStack(labels.some((l) => labelTextWidth(l) > half - STRIPE_PAIR_SLACK));
  }, [p.fit, fieldH]);
  const [sized, setSized] = useState(false);
  const refitDone = useRef(false);
  const answered = formSettled && pay.expressState !== 'wait';
  useLayoutEffect(() => {
    if (!answered || sized) return;
    const fits = () => {
      // Дробные высоты: Stripe дорастает 198 → 277 → 276,3, плашки — 276,25; сравнение до 0,5 px.
      // Невидимая обёртка (абсолютная) и коробка в потоке (`flow-root`) одной высоты — сравниваем
      // её с плашками, дробно, до 0,5 px (Stripe дорастает 191 → 268,3).
      const h = (el: Element | null | undefined) => el?.getBoundingClientRect().height ?? 0;
      const skel = h(skelRef.current);
      // Невидимая абсолютная обёртка = коробка формы в потоке (iframe − 8 px, `flow-root`).
      const real = h(formPendRef.current);
      const formOk = !pay?.formReady || skel === 0 || Math.abs(real - skel) < 0.5;
      // Форма Stripe легла другой раскладкой («Срок/Код» в строку вместо столбика или наоборот):
      // плашки перекладываются ОДИН раз — до подмены, пока полей ещё нет; подмена дождётся.
      if (!formOk && pay?.formReady && !pairFlipped.current && pairStack !== null) {
        const pair = labelLineNow + fieldH;
        const other = pairStack ? 2 * pair + 12.75 : 3 * pair + 2 * 12.75;
        if (Math.abs(real - other) < 0.5) {
          pairFlipped.current = true;
          setPairStack(!pairStack);
        }
      }
      // Плашка ряда (её место — коробка `.pay-row`) против невидимого ряда Stripe.
      const rowBox = h(rowPendRef.current?.parentElement);
      const rowOk = pay?.expressState !== 'yes' || rowBox === 0 || h(rowPendRef.current) >= rowBox - 0.5;
      return formOk && rowOk;
    };
    // В момент подмены — один пересчёт уступок по ФАКТИЧЕСКИМ высотам (ревизия п.6): оценка при
    // открытии брала плашки, а Stripe кладёт поля по-своему (в WebKit «Срок/Код» в строку), ряд мог
    // исчезнуть. Изменились только уступки 1–3 — применяются тем же кадром, что подмена; задета
    // уступка 4 (подписи формы) — форма перерисуется, подмена ждёт её новой высоты.
    const finish = (): boolean => {
      if (!fits()) return false;
      if (!refitDone.current) {
        refitDone.current = true;
        const steps = refit();
        if (steps !== null && steps !== p.fit) {
          p.onRefit(steps);
          if (steps >= 4 !== p.fit >= 4) return false;
        }
      }
      setSized(true);
      return true;
    };
    if (finish()) return;
    // ВЕСЬ СЛОЙ ПОД ЗАМКОМ (§14.4): совпадать с плашками форме не обязательно — их не видно. Ждём лишь, пока
    // форма и ряд перестанут меняться (SETTLE_MS без изменения размера), затем один пересчёт уступок и
    // подмена как есть (владелец 09.10: ожидание совпадения высот доходило до SIZE_MAX_MS).
    const force = () => {
      if (!refitDone.current) {
        refitDone.current = true;
        const steps = refit();
        if (steps !== null && steps !== p.fit) {
          p.onRefit(steps);
          if (steps >= 4 !== p.fit >= 4) return; // форма перерисуется — эффект повторится с новым p.fit
        }
      }
      // Высоты так и не совпали (WebKit, дроби, другая раскладка «Срок/Код»): плашки берут высоту формы
      // кадром РАНЬШЕ подмены — иначе всё ниже формы сдвигается на разницу (layout-shift 0,03 в тесте).
      const skel = skelRef.current;
      const real = formPendRef.current?.getBoundingClientRect().height ?? 0;
      if (skel && real > 0 && Math.abs(skel.getBoundingClientRect().height - real) >= 0.5) {
        skel.style.height = `${real}px`;
        skel.style.overflow = 'hidden';
        requestAnimationFrame(() => requestAnimationFrame(() => setSized(true)));
        return;
      }
      setSized(true);
    };
    let settle = window.setTimeout(force, SETTLE_MS);
    const ro = new ResizeObserver(() => {
      window.clearTimeout(settle);
      if (finish()) return;
      settle = window.setTimeout(force, SETTLE_MS);
    });
    if (formPendRef.current) ro.observe(formPendRef.current);
    if (rowPendRef.current) ro.observe(rowPendRef.current);
    const cap = window.setTimeout(() => setSized(true), SIZE_MAX_MS);
    return () => {
      ro.disconnect();
      window.clearTimeout(settle);
      window.clearTimeout(cap);
    };
    // refit/fits читают узлы и уступки на момент вызова; p.fit — повторный проход после уступки 4.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answered, sized, pay?.formReady, pay?.expressState, p.fit, pairStack]);
  const swapped = answered && sized;
  const rowLive = !!pay && pay.expressState === 'yes' && swapped;
  // Ряд в раскладке: Stripe ещё не ответил (плашка) или ответил «есть кнопки». До подмены ряд
  // показывается плашкой даже без формы Stripe (ничего не загрузилось — тогда ряда нет вовсе).
  // Ответ «кнопок нет» до подмены ряд не убирает: плашка держит место до того же кадра, что встаёт форма
  // (§3.2), иначе всё ниже сдвинулось бы под пальцем раньше подмены.
  const rowShown =
    !p.loadFailed &&
    (pay
      ? pay.express !== null && (!swapped || pay.expressState !== 'no')
      : expressAnyOn(expressMethods(p.wallets, p.choice.autorenew === true)));
  const formLive = swapped && !!pay?.formReady;
  const formError = p.loadFailed || !!pay?.formFailed;
  const busy = pay?.cta === 'busy' || pay?.cta === 'done';

  // ВМЕСТИМОСТЬ (§1.3): один раз при открытии — по видимой высоте прокрутки слоя (`.dr-body`) и
  // высоте содержимого с плашками; уступки по порядку, пока не влезет. Замер — когда снят вид поля
  // (высота плашек = высоте формы Stripe), не раньше.
  useLayoutEffect(() => {
    const el = root.current;
    if (!el || !p.field || pairStack === null || fitDone.current) return;
    fitDone.current = true;
    const m = measureContent(el);
    if (!m) return;
    const { contentH, avail } = m;
    const need = Math.round(contentH);
    const secure = el.querySelector<HTMLElement>('.pay-secure');
    const savings = [
      Math.max(0, (secure?.offsetHeight ?? 17) - 17),
      // Уступка 2 («сводка плательщика в одну строку») снята: плательщик пишется целиком, до трёх строк
      // узким интервалом (владелец 09.10) — экономии нет.
      0,
      // Отступы между группами по минимуму (`.pay-fit3` в cabinet.css: 8) — сумма фактических
      // излишков нижних полей групп слоя.
      Array.from(el.querySelectorAll<HTMLElement>(':scope > .k-lsec, :scope > .k-lblock, :scope > .pay-row, :scope > .pay-form'))
        .map((x) => Math.max(0, num(getComputedStyle(x).marginBottom) - 8))
        .reduce((a, b) => a + b, 0),
      // Подписи формы 12/4 вместо своих — вместе с возможным переходом «Срок/Код» в строку.
      step4Saving(el.querySelector<HTMLElement>(':scope > .pay-form')?.offsetWidth ?? 0),
    ];
    const plan = fitsPlan(avail, need, savings);
    fitData.current = { savings };
    // Числа замера — атрибутом слоя для промеров вместимости (маршрут N67): видимая высота,
    // нужная, уступки по порядку, сколько применено, влезло ли.
    el.dataset.payFit = JSON.stringify({ avail, need, savings: savings.map(Math.round), ...plan });
    p.onFit(plan.steps as FitSteps);
    // Только при открытии слоя (§1.3): дальнейшие правки высоты — действия клиента.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.field, pairStack]);

  return (
    <div
      ref={root}
      // Состояние подмены для промеров (tools/pay-shift-probe.mjs): форма ответила · ряд · высоты набраны.
      data-pay-state={`${formSettled ? 'form' : 'wait'}:${pay?.expressState ?? 'none'}:${sized ? 'sized' : 'grow'}`}
      className={`pay-layer${p.fit >= 3 ? ' pay-fit3' : ''}${p.fit >= 4 ? ' pay-fit4' : ''}${!formLive && !formError ? ' pay-loading' : ''}`}
      inert={busy ? true : undefined}>
      {/* РЯД КОШЕЛЬКОВ (§1.2 п.2) — место высоты кнопки, пока Stripe не ответил; кнопок нет —
          ни ряда, ни разделителя, места под них нет. Пока ряд и форма грузятся — блок с замком (§14.4). */}
      <PayLoading active={!formLive && !formError} />
      {rowShown && (
        <>
          <div className="pay-row">
            {!rowLive && <KSkeleton height={p.buttonHeight} radius="round" />}
            {pay?.express && (
              <div ref={rowPendRef} className={rowLive ? 'pay-live' : 'pay-pend'}>
                {pay.express}
              </div>
            )}
          </div>
          <div className={`pay-or${rowLive ? '' : ' pay-wait'}`}>
            <KListNote>{t('payOr')}</KListNote>
          </div>
        </>
      )}
      {/* ФОРМА КАРТЫ (§1.2 п.5) — три плашки с подписями «Номер карты», «Срок», «Код» высотой поля;
          Payment Element под ними невидим, пока не готов. */}
      <div className="pay-form">
        {formError ? (
          <DError>{t('errGeneric')}</DError>
        ) : (
          <>
            {!formLive && (
              <div ref={skelRef} className="pay-skel pay-skel--lock">
                <KSkeletonField label={t('payCardNumber')} height={fieldH} />
                <div ref={pairRef} className={pairStack ? 'pay-skel__pair pay-skel__pair--stack' : 'pay-skel__pair'}>
                  <KSkeletonField label={t('payCardExpiry')} height={fieldH} />
                  <KSkeletonField label={t('payCardCvc')} height={fieldH} />
                </div>
              </div>
            )}
            {pay && (
              <div ref={formPendRef} className={formLive ? 'pay-live' : 'pay-pend'}>
                {pay.form}
              </div>
            )}
          </>
        )}
      </div>
      {/* «Сохранить карту» и «Плательщик» — на экране подтверждения (§18.2): в форме их нет. */}
      {pay?.err && <DError>{pay.err}</DError>}
      <KListBlock>
        <PayCta
          state={pay?.cta ?? 'idle'}
          price={p.priceLabel}
          disabled={!pay?.canPay || formError}
          onClick={() => pay?.onPay()}
        />
      </KListBlock>
      {/* Под кнопкой формы — «Защищённая оплата Stripe» (§18.2 экран 3); полная строка защиты — на экране расчёта. */}
      <PayLockNote short />
    </div>
  );
}

/**
 * ЗАГРУЗКА ФОРМЫ — ЯВНО И «ЗАЩИЩЁННО» (PAY-FLOW-TZ §14.4, §16 К3): на месте формы, той же высоты (её держат
 * невидимые плашки — подмена без сдвига), по центру — герой приложения (`ScrHero` → веб `SettingsHero`): замок 56
 * серым тонким контуром мягко «дышит» (при reduced-motion — без анимации), «Загружаем форму оплаты…» и подпись о
 * защищённом соединении.
 */
function PayLoading({ active }: { active: boolean }) {
  const t = useTranslations('cabinet.billing');
  // ВЕСЬ СЛОЙ ПОД ЗАМКОМ (§14.4, приёмка 09.10, как Stripe Checkout): пока ряд кошельков не ответил и форма не
  // готова, всё тело слоя (ряд, форма, подпись, «Плательщик», «Оплатить», строка защиты) невидимо — их место
  // держит прежняя раскладка, — а блок с замком стоит по центру видимой области листа. Подмена показывает
  // итоговый вид целиком одним кадром: видимого сдвига нет, даже если ряда кошельков в итоге не будет.
  // Слой — через свой элемент (родитель блока): ref хозяина ставится позже эффекта разметки ребёнка.
  const self = useRef<HTMLDivElement | null>(null);
  const [h, setH] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = self.current?.parentElement;
    const body = el?.closest<HTMLElement>('.dr-body');
    if (!active || !el || !body) return;
    const calc = () => {
      const pad = parseFloat(getComputedStyle(body).paddingBottom) || 0;
      // Видимая область тела листа от верха слоя — блок по её центру (слой бывает выше экрана).
      // Числа раскладки, не экрана: лист въезжает трансформом (сдвиг и масштаб), и разница экранных
      // прямоугольников во время въезда плыла — блок «ехал» вместе с анимацией (видео владельца 09.10).
      // Масштаб снимается отношением clientHeight тела к его экранной высоте.
      const bodyRect = body.getBoundingClientRect();
      const k = bodyRect.height > 0 ? body.clientHeight / bodyRect.height : 1;
      const vis = body.clientHeight - pad - (el.getBoundingClientRect().top - bodyRect.top) * k;
      const next = Math.round(Math.max(0, Math.min(el.offsetHeight, vis)));
      setH((cur) => (cur === next ? cur : next));
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    ro.observe(body);
    return () => ro.disconnect();
  }, [active]);
  if (!active) return null;
  return (
    <div ref={self} className="pay-lock" role="status" style={h ? { top: 0, height: h } : { visibility: 'hidden' }}>
      {/* К3 (PAY-FLOW-TZ §16): ровно герой приложения (`ScrHero`) — его веб-зеркало `SettingsHero` (.set-hero),
          своих чисел у блока нет; замок серым контуром, «дышит». */}
      <SettingsHero icon={<LockIcon />} title={t('payLoading')} meta={t('payLoadingNote')} />
    </div>
  );
}

/** Кнопка «Оплатить {сумма}» (§1.4 п.4): покой — замок и сумма; платёж — кружок и «Обрабатываем…»;
 *  успех — галочка и «Готово». Ширина и высота не меняются: кнопка роли «действие» во всю ширину,
 *  ширина фиксируется при нажатии (`min-width`). */
export function PayCta({
  state,
  price,
  disabled,
  onClick,
  label,
  busyLabel,
}: {
  state: CtaState;
  price: string;
  disabled: boolean;
  onClick: () => void;
  /** Своя подпись покоя (рубли: «Перейти к оплате …»); по умолчанию «Оплатить {сумма}». */
  label?: string;
  /** Своя подпись платежа (рубли, §18.2: «Подготовка оплаты…»). */
  busyLabel?: string;
}) {
  const t = useTranslations('cabinet.billing');
  const ref = useRef<HTMLDivElement | null>(null);
  const [minW, setMinW] = useState<number | null>(null);
  // Платёж готовится — кружок-индикатор всегда (§18.1 п.9: «спиннер, который явно даёт понять, что сейчас откроется
  // страница»); подпись — своя у рублей («Подготовка оплаты…»).
  const icon =
    state === 'busy' ? (
      <span className="pay-cta-spin" aria-hidden />
    ) : state === 'done' ? (
      <DoneIcon />
    ) : (
      <LockIcon />
    );
  return (
    <div ref={ref} className="pay-cta" style={minW ? { minWidth: minW } : undefined}>
      <KButton
        tone="primary"
        action
        className="cta-grad"
        icon={icon}
        aria-busy={state === 'busy' || undefined}
        disabled={disabled || state !== 'idle'}
        onClick={() => {
          setMinW(ref.current?.getBoundingClientRect().width ?? null);
          onClick();
        }}
      >
        {state === 'busy' ? (busyLabel ?? t('processing')) : state === 'done' ? t('payDone') : (label ?? t('payCtaFirst', { price }))}
      </KButton>
    </div>
  );
}

function DoneIcon() {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4.5 12.5 9.5 17.5 19.5 5.5" />
    </svg>
  );
}

/** Строка защиты под кнопкой (§1.2 п.9): замок и текст; `short` — уступка вместимости §1.3 п.1. */
export function PayLockNote({ short, text }: { short?: boolean; text?: string }) {
  const t = useTranslations('cabinet.billing');
  return (
    <div className="pay-secure">
      <KListNote>
        {/* Обёртка значков набора (inline-flex): svg в строке текста, а не отдельным блоком. */}
        <span className="k-btn__icon">
          <LockIcon />
        </span>{' '}
        {text ?? (short ? t('paySecureShort') : t('paySecureNote'))}
      </KListNote>
    </div>
  );
}

/** Тело нажатия экрана подтверждения (PAY-FLOW-TZ §18.2, `billing-check.tsx`) — одно на рубли и евро: сохранённой
 *  картой — `saved`, иначе новая карта; `bindCard` — «Сохранить карту» (при автопродлении — всегда). */
export type CheckPayBody = { saved: boolean; bindCard: boolean; autorenew: boolean | null; via: 'card' | 'saved' };

// ─────────────────────────── «Добавить карту» без оплаты (PAY-FLOW-TZ §6.5) ───────────────────────────

export interface AddCardSheetProps {
  publishableKey: string;
  /** Валюта подписки (евро или доллары). */
  currency: Currency;
  /** Тумблеры кошельков админки; PayPal тут нет (только карта, §6.5). */
  wallets: PayWallets;
  /** Следующее списание — для Apple Pay (§3.6) и подписи. */
  next: PayNext | null;
  /** Подпись «Деньги сейчас не спишутся. … спишется …» — готовая (дата и сумма из сводки). */
  note: string;
  payer: PayerProfile | null;
  lock: CardSheetLock;
  /** Stripe принял карту (3-D Secure, если был, пройден) — хозяин закрывает слой и ждёт вебхук. */
  onSaved: (setupId: string) => void;
}

/** Слой «Добавить карту»: в приложении не открывается (тот же замок, что у слоя «Оплата»). */
export function AddCardSheet(props: AddCardSheetProps) {
  const native = useNativeApp();
  if (native || isNativeApp()) return null;
  return <AddCardBody {...props} />;
}

function AddCardBody(props: AddCardSheetProps) {
  const locale = useLocale();
  const { probe, field, palette, stripeP, loadFailed, buttonHeight, dark } = useStripeLook(props.publishableKey, props.lock);
  const appearance = useMemo<Appearance | null>(
    () => (field && palette ? stripeAppearance(palette.value, field, { walletHeight: buttonHeight }) : null),
    [field, palette, buttonHeight],
  );
  const options = useMemo(
    () =>
      appearance
        ? {
            ...setupElementsOptions(props.currency),
            appearance,
            locale: (locale === 'en' ? 'en' : 'ru') as 'en' | 'ru',
            fonts: [{ cssSrc: `${window.location.origin}${assetUrl('/fonts/onest.css')}` }],
          }
        : null,
    [appearance, props.currency, locale],
  );
  return (
    <FPane>
      <StripeProbe probe={probe} />
      {!loadFailed && options && stripeP ? (
        <Elements key={locale} stripe={stripeP} options={options}>
          <AddCardForm {...props} buttonHeight={buttonHeight} dark={dark} fieldH={Math.round(field?.height ?? 52)} />
        </Elements>
      ) : (
        <AddCardLayout {...props} fieldH={Math.round(field?.height ?? 52)} buttonHeight={buttonHeight} failed={loadFailed} pay={null} />
      )}
    </FPane>
  );
}

interface AddCardParts {
  express: ReactNode | null;
  expressState: 'wait' | 'yes' | 'no';
  form: ReactNode;
  formReady: boolean;
  formFailed: boolean;
  cta: CtaState;
  err: string | null;
  onSave: () => void;
}

function AddCardForm(p: AddCardSheetProps & { buttonHeight: number; dark: boolean; fieldH: number }) {
  const t = useTranslations('cabinet.billing');
  const locale = useLocale();
  const stripe = useStripe();
  const elements = useElements();
  const { payer: cached } = usePayer();
  const payer = cached ?? p.payer;
  const [peOptions] = useState(() => ({
    layout: 'tabs' as const,
    paymentMethodOrder: ['card'],
    wallets: { applePay: 'never' as const, googlePay: 'never' as const, link: 'never' as const },
    fields: { billingDetails: cardFieldsOption(payer) },
    defaultValues: {
      billingDetails: { name: payer?.name ?? '', email: payer?.email ?? '', phone: payer?.phone ?? '', address: { country: payer?.country ?? '' } },
    },
    terms: { card: 'never' as const },
  }));
  const [formReady, setFormReady] = useState(false);
  const [formFailed, setFormFailed] = useState(false);
  const [express, setExpress] = useState<'wait' | 'yes' | 'no'>('wait');
  const [cta, setCta] = useState<CtaState>('idle');
  const [err, setErr] = useState<string | null>(null);
  // Только Apple Pay / Google Pay: карта сохраняется, PayPal в режиме карты не участвует (§6.5).
  const methods = useMemo(() => expressMethods({ ...p.wallets, paypal: false }, true), [p.wallets]);
  const anyOn = expressAnyOn(methods);
  const rowState: 'wait' | 'yes' | 'no' = !anyOn ? 'no' : express;
  const setRow = (next: 'yes' | 'no') => setExpress((cur) => (cur === 'no' ? cur : next));
  useLayoutEffect(() => {
    if (formReady || formFailed) return;
    const id = window.setTimeout(() => {
      setFormFailed(true);
      reportUiError('payment_stripe_client', `stripe: форма карты (setup) не загрузилась за ${LOAD_MAX_MS} мс`, { step: 'load' });
      payErrorBuzz();
    }, LOAD_MAX_MS);
    return () => window.clearTimeout(id);
  }, [formReady, formFailed]);
  useLayoutEffect(() => {
    if (!(formReady || formFailed) || rowState !== 'wait') return;
    const id = window.setTimeout(() => setExpress((cur) => (cur === 'wait' ? 'no' : cur)), EXPRESS_WAIT_MS);
    return () => window.clearTimeout(id);
  }, [formReady, formFailed, rowState]);

  const fail = (text: string) => {
    setErr(text);
    payErrorBuzz();
  };
  const stripeFail = (e: { type?: string; message?: string; code?: string } | null, step: string) => {
    const text = stripeErrorForClient(e, t('errGeneric'), { step, purpose: 'card-setup' });
    if (e?.type === 'card_error' || e?.type === 'validation_error') payErrorBuzz();
    else fail(text);
  };
  const finish = (id: string) => {
    setCta('done');
    window.setTimeout(() => {
      p.lock.set(false);
      p.onSaved(id);
    }, DONE_MS);
  };

  /** `submit` → `POST /api/billing/card/setup` (договор §7.4) → `confirmSetup` (банк может спросить
   *  подтверждение — окно поверх). Деньги не списываются. */
  const save = async (withPayer: boolean): Promise<boolean> => {
    if (!stripe || !elements || !payer) return false;
    const sub = await elements.submit();
    if (sub.error) {
      if (!withPayer) stripeFail(sub.error, 'setup:wallet');
      return false;
    }
    const r = parseCardSetup(await postJson('/api/billing/card/setup', {}).catch(() => null));
    if (!r) {
      fail(t('errGeneric'));
      return false;
    }
    const { error } = await stripe.confirmSetup({
      elements,
      clientSecret: r.clientSecret,
      confirmParams: {
        return_url: subscriptionUrl(window.location.origin, locale),
        ...(withPayer ? { payment_method_data: { billing_details: payerBillingDetails(payer) } } : {}),
      },
      redirect: 'if_required',
    });
    if (error) {
      stripeFail(error, 'confirmSetup');
      return false;
    }
    finish(r.id);
    return true;
  };

  const onSave = async () => {
    if (p.lock.busy()) return;
    p.lock.set(true);
    setCta('busy');
    setErr(null);
    let ok = false;
    try {
      ok = await save(true);
    } catch (e) {
      stripeFail({ message: e instanceof Error ? e.message : String(e) }, 'setup');
    } finally {
      if (!ok) {
        p.lock.set(false);
        setCta('idle');
      }
    }
  };

  const expressNode = anyOn ? (
    <ExpressCheckoutElement
      options={{
        buttonHeight: p.buttonHeight,
        buttonType: { applePay: 'plain', googlePay: 'plain', paypal: 'paypal' },
        buttonTheme: expressButtonTheme(p.dark),
        paymentMethods: methods,
        layout: { maxColumns: 2, maxRows: 1, overflow: 'auto' },
      }}
      onReady={(ev) => setRow(expressAvailable(ev.availablePaymentMethods) ? 'yes' : 'no')}
      onAvailablePaymentMethodsChange={(ev) => setRow(expressAvailable(ev.paymentMethods) ? 'yes' : 'no')}
      onClick={(ev) => {
        if (p.lock.busy() || !payer) return ev.reject();
        if (ev.expressPaymentType === 'apple_pay' && p.next) {
          return ev.resolve({
            applePay: applePayRecurring({
              description: t('applePayDesc', { n: p.next.accounts }),
              managementURL: subscriptionUrl(window.location.origin, locale),
              label: t('applePayRenewLabel'),
              amount: p.next.amount,
              currency: p.currency,
              period: p.next.period,
              start: p.next.date,
            }),
          });
        }
        ev.resolve({});
      }}
      onConfirm={(ev) =>
        void (async () => {
          if (p.lock.busy()) return ev.paymentFailed({ reason: 'fail' });
          p.lock.set(true);
          setErr(null);
          let ok = false;
          try {
            ok = await save(false);
          } catch (x) {
            stripeFail({ message: x instanceof Error ? x.message : String(x) }, 'setup:wallet');
          } finally {
            if (!ok) {
              p.lock.set(false);
              ev.paymentFailed({ reason: 'fail' });
            }
          }
        })()
      }
      onLoadError={() => setRow('no')}
    />
  ) : null;

  const formNode = (
    <PaymentElement
      options={peOptions}
      onReady={() => setFormReady(true)}
      onLoadError={(ev) => {
        setFormFailed(true);
        stripeErrorForClient(ev.error, t('errGeneric'), { step: 'load', purpose: 'card-setup' });
        payErrorBuzz();
      }}
    />
  );

  return (
    <AddCardLayout
      {...p}
      payer={payer}
      failed={false}
      pay={{
        express: expressNode,
        expressState: rowState,
        form: formNode,
        formReady,
        formFailed,
        cta,
        err,
        onSave: () => void onSave(),
      }}
    />
  );
}

/** Раскладка «Добавить карту» (сверху вниз по §6.5): ряд Apple Pay / Google Pay, «или картой», форма,
 *  подпись «Деньги сейчас не спишутся…», плательщик, «Сохранить карту», строка защиты. Пока Stripe не
 *  ответил — плашки на месте ряда и формы; подмена — одним кадром, когда ответили оба. */
function AddCardLayout(
  p: AddCardSheetProps & { fieldH: number; buttonHeight: number; failed: boolean; pay: AddCardParts | null },
) {
  const t = useTranslations('cabinet.billing');
  const pay = p.pay;
  const answered = !!pay && (pay.formReady || pay.formFailed) && pay.expressState !== 'wait';
  const formError = p.failed || !!pay?.formFailed;
  const busy = pay?.cta === 'busy' || pay?.cta === 'done';
  // «Срок» и «Код» плашек — как их положит Stripe: в строку, если подписи влезают в половину.
  const pairRef = useRef<HTMLDivElement | null>(null);
  const skelRef = useRef<HTMLDivElement | null>(null);
  const formPendRef = useRef<HTMLDivElement | null>(null);
  const [pairStack, setPairStack] = useState(false);
  const pairFlipped = useRef(false);
  useLayoutEffect(() => {
    const el = pairRef.current;
    if (!el || pairFlipped.current) return;
    const half = (el.offsetWidth - 12) / 2;
    setPairStack(
      Array.from(el.querySelectorAll<HTMLElement>('.k-skel-field__label')).some((l) => labelTextWidth(l) > half - STRIPE_PAIR_SLACK),
    );
  }, [p.fieldH]);
  // ПОДМЕНА БЕЗ СДВИГА (то же правило, что у слоя «Оплата», §3.2): ответ Stripe ≠ конечная высота — заглушки
  // уходят, когда невидимая форма набрала их высоту (до 0,5 px; легла другой раскладкой «Срок/Код» — плашки
  // перекладываются один раз до подмены), не дольше SIZE_MAX_MS. Ряд без кнопок убирается тем же кадром.
  const [sized, setSized] = useState(false);
  useLayoutEffect(() => {
    if (!answered || sized) return;
    const h = (el: Element | null | undefined) => el?.getBoundingClientRect().height ?? 0;
    const fits = (): boolean => {
      if (!pay?.formReady) return true;
      const skel = h(skelRef.current);
      const real = h(formPendRef.current);
      if (skel === 0 || Math.abs(real - skel) < 0.5) return true;
      if (!pairFlipped.current) {
        const label = (pairRef.current?.querySelector('.k-skel-field__label')?.getBoundingClientRect().height ?? 0) + 8;
        const pair = label + p.fieldH;
        const other = pairStack ? 2 * pair + 12.75 : 3 * pair + 2 * 12.75;
        if (Math.abs(real - other) < 1.5) {
          pairFlipped.current = true;
          setPairStack(!pairStack);
        }
      }
      return false;
    };
    if (fits()) {
      setSized(true);
      return;
    }
    // Под замком плашек не видно: не совпали — ждём лишь, пока форма перестанет расти (SETTLE_MS); плашки
    // берут высоту формы кадром раньше подмены (ниже формы ничего не сдвигается), затем подмена.
    const force = () => {
      const skel = skelRef.current;
      const real = h(formPendRef.current);
      if (skel && real > 0 && Math.abs(h(skel) - real) >= 0.5) {
        skel.style.height = `${real}px`;
        skel.style.overflow = 'hidden';
        requestAnimationFrame(() => requestAnimationFrame(() => setSized(true)));
        return;
      }
      setSized(true);
    };
    let settle = window.setTimeout(force, SETTLE_MS);
    const ro = new ResizeObserver(() => {
      window.clearTimeout(settle);
      if (fits()) setSized(true);
      else settle = window.setTimeout(force, SETTLE_MS);
    });
    if (formPendRef.current) ro.observe(formPendRef.current);
    if (skelRef.current) ro.observe(skelRef.current);
    const cap = window.setTimeout(() => setSized(true), SIZE_MAX_MS);
    return () => {
      ro.disconnect();
      window.clearTimeout(settle);
      window.clearTimeout(cap);
    };
  }, [answered, sized, pay?.formReady, pairStack, p.fieldH]);
  const swapped = answered && sized;
  // Ряд: плашка до подмены; после — только если кнопки есть.
  const rowShown =
    !p.failed && (pay ? pay.express !== null && (!swapped || pay.expressState === 'yes') : p.wallets.applePay || p.wallets.googlePay);
  return (
    <div className={`pay-layer${!(swapped && !!pay?.formReady) && !formError ? ' pay-loading' : ''}`} inert={busy ? true : undefined}>
      <PayLoading active={!(swapped && !!pay?.formReady) && !formError} />
      {rowShown && (
        <>
          <div className="pay-row">
            {!(swapped && pay?.expressState === 'yes') && <KSkeleton height={p.buttonHeight} radius="round" />}
            {pay?.express && <div className={swapped && pay.expressState === 'yes' ? 'pay-live' : 'pay-pend'}>{pay.express}</div>}
          </div>
          <div className={`pay-or${swapped && pay?.expressState === 'yes' ? '' : ' pay-wait'}`}>
            <KListNote>{t('payOr')}</KListNote>
          </div>
        </>
      )}
      <div className="pay-form">
        {formError ? (
          <DError>{t('errGeneric')}</DError>
        ) : (
          <>
            {!(swapped && pay?.formReady) && (
              <div ref={skelRef} className="pay-skel pay-skel--lock">
                <KSkeletonField label={t('payCardNumber')} height={p.fieldH} />
                <div ref={pairRef} className={pairStack ? 'pay-skel__pair pay-skel__pair--stack' : 'pay-skel__pair'}>
                  <KSkeletonField label={t('payCardExpiry')} height={p.fieldH} />
                  <KSkeletonField label={t('payCardCvc')} height={p.fieldH} />
                </div>
              </div>
            )}
            {pay && (
              <div ref={formPendRef} className={swapped && pay.formReady ? 'pay-live' : 'pay-pend'}>
                {pay.form}
              </div>
            )}
          </>
        )}
      </div>
      <div className="pay-save">
        <KListNote>{p.note}</KListNote>
      </div>
      {pay?.err && <DError>{pay.err}</DError>}
      <PayerSummary fallback={p.payer} disabled={busy} />
      <KListBlock>
        <PayCta
          state={pay?.cta ?? 'idle'}
          price=""
          label={t('payBindCard')}
          disabled={!pay || !swapped || !pay.formReady || formError}
          onClick={() => pay?.onSave()}
        />
      </KListBlock>
      <PayLockNote short />
    </div>
  );
}

/** Для юнитов: тип `elements` в обработчиках (не рантайм). */
export type { StripeElements };
